import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import apiService from '../../services/apiService';
import { calcularDuracionMinutos, formatDuration } from '../../utils/timeHelpers';
import EvidenciaGaleria from './EvidenciaGaleria';

/**
 * Checklist de actividades de una orden de trabajo con ejecucion en terreno.
 * Muestra una actividad a la vez con navegación por pasos.
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} props.ordenTrabajoId - ID de la orden de trabajo
 * @param {string} props.ordenTrabajoEstado - Estado de la OT (deshabilita acciones si completado/cancelado)
 * @param {number|null} props.plantillaId - ID de plantilla aplicada (null = mostrar "Aplicar Plantilla")
 * @param {Function} props.onRefresh - Callback para refrescar datos de la OT padre
 * @param {number} props.refreshTrigger - Cambiar valor para forzar recarga
 * @returns {JSX.Element}
 */
function ActividadesOrdenTrabajoList({ ordenTrabajoId, ordenTrabajoEstado, plantillaId, onRefresh, refreshTrigger = 0 }) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plantillas, setPlantillas] = useState([]);
  const [selectedPlantillaId, setSelectedPlantillaId] = useState('');
  const [copyingPlantilla, setCopyingPlantilla] = useState(false);
  const [omitirActId, setOmitirActId] = useState(null);
  const [omitirObs, setOmitirObs] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);
  const [, setTick] = useState(0);
  const initializedRef = useRef(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const disabled = ordenTrabajoEstado === 'completado' || ordenTrabajoEstado === 'cancelado';

  const fetchActividades = useCallback(async () => {
    if (!ordenTrabajoId) return;
    setLoading(true);
    try {
      const response = await apiService.getActividadesOrdenTrabajo(ordenTrabajoId);
      if (response.success) {
        setActividades(response.data || []);
      }
    } catch (error) {
      showToast('Error al cargar actividades', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenTrabajoId, showToast, refreshTrigger]);

  useEffect(() => {
    fetchActividades();
  }, [fetchActividades]);

  // Resetear inicialización al cambiar de OT
  useEffect(() => {
    initializedRef.current = false;
    setCurrentIndex(0);
  }, [ordenTrabajoId]);

  // Al cargar actividades por primera vez, saltar al primer paso activo
  useEffect(() => {
    if (actividades.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      const firstActive = actividades.findIndex(a => a.estado === 'pendiente' || a.estado === 'en_progreso');
      setCurrentIndex(firstActive >= 0 ? firstActive : 0);
    }
  }, [actividades]);

  // Cargar plantillas si no hay plantilla aplicada
  useEffect(() => {
    if (!plantillaId) {
      apiService.getPlantillasMantenimientoLista()
        .then(res => { if (res.success) setPlantillas(res.data || []); })
        .catch(() => {});
    }
  }, [plantillaId]);

  // Timer para duracion en vivo de actividades en_progreso
  useEffect(() => {
    const hasEnProgreso = actividades.some(a => a.estado === 'en_progreso');
    if (hasEnProgreso) {
      timerRef.current = setInterval(() => setTick(t => t + 1), 60000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [actividades]);

  /**
   * Copia actividades de plantilla a la orden de trabajo
   */
  const handleCopiarPlantilla = async () => {
    if (!selectedPlantillaId) return;
    setCopyingPlantilla(true);
    try {
      const response = await apiService.copiarPlantillaAOrdenTrabajo(ordenTrabajoId, selectedPlantillaId);
      if (response.success) {
        showToast(response.message, 'success');
        fetchActividades();
        if (onRefresh) onRefresh();
      } else {
        showToast(response.message || 'Error al aplicar plantilla', 'error');
      }
    } catch (error) {
      showToast('Error al aplicar plantilla', 'error');
    } finally {
      setCopyingPlantilla(false);
    }
  };

  /**
   * Cambia estado de una actividad y avanza al siguiente paso si corresponde
   * @param {number} actividadId - ID de la actividad cuyo estado se cambia
   * @param {string} estado - Nuevo estado: 'en_progreso' | 'completado' | 'omitido'
   * @param {string} [observaciones] - Texto opcional para describir la transición
   */
  const handleCambiarEstado = async (actividadId, estado, observaciones) => {
    try {
      const data = { estado };
      if (observaciones) data.observaciones = observaciones;
      const response = await apiService.cambiarEstadoActividad(ordenTrabajoId, actividadId, data);
      if (response.success) {
        showToast(response.message, 'success');
        if ((estado === 'completado' || estado === 'omitido') && currentIndex < actividades.length - 1) {
          setCurrentIndex(i => i + 1);
        }
        fetchActividades();
        if (onRefresh) onRefresh();
      } else {
        showToast(response.message || 'Error al cambiar estado', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Error al cambiar estado', 'error');
    }
  };

  /**
   * Obtiene duracion formateada de una actividad
   * @param {Object} act - Actividad con campos estado, fecha_inicio y fecha_completado
   * @returns {string}
   */
  const getDuracion = (act) => {
    if (act.estado === 'completado' && act.fecha_inicio) {
      return formatDuration(calcularDuracionMinutos(act.fecha_inicio, act.fecha_completado));
    }
    if (act.estado === 'en_progreso' && act.fecha_inicio) {
      return formatDuration(calcularDuracionMinutos(act.fecha_inicio, new Date()));
    }
    return '-';
  };

  const estadoBadge = {
    pendiente: 'badge-secondary',
    en_progreso: 'badge-info',
    completado: 'badge-success',
    omitido: 'badge-warning'
  };

  const estadoLabel = {
    pendiente: 'Pendiente',
    en_progreso: 'En progreso',
    completado: 'Completado',
    omitido: 'Omitido'
  };

  const completadas = actividades.filter(a => a.estado === 'completado').length;
  const total = actividades.length;
  const progreso = total > 0 ? Math.round((completadas / total) * 100) : 0;

  const safeIndex = Math.min(currentIndex, Math.max(0, actividades.length - 1));
  const act = actividades[safeIndex];

  const handlePrev = () => {
    setOmitirActId(null);
    setOmitirObs('');
    setCurrentIndex(i => Math.max(0, i - 1));
  };

  const handleNext = () => {
    setOmitirActId(null);
    setOmitirObs('');
    setCurrentIndex(i => Math.min(actividades.length - 1, i + 1));
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx < 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const handleDotClick = (idx) => {
    setOmitirActId(null);
    setOmitirObs('');
    setCurrentIndex(idx);
  };

  if (loading) {
    return (
      <div className="actividades-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Cargando actividades...</span>
      </div>
    );
  }

  return (
    <div className="actividades-plantilla-container">
      {/* Barra de progreso */}
      {total > 0 && (
        <div className="mant-progress-section">
          <div className="mant-progress-header">
            <span className={`mant-progress-text ${progreso === 100 ? 'mant-progress-text--complete' : ''}`}>
              {completadas} de {total} completadas
            </span>
          </div>
          <div className="mant-progress-bar">
            <div className="mant-progress-fill" style={{ width: `${progreso}%` }}></div>
          </div>
        </div>
      )}

      {/* Aplicar plantilla (solo si no tiene) */}
      {!plantillaId && actividades.length === 0 && !disabled && (
        <div className="actividades-scroll-area">
          <div className="mant-plantilla-selector">
            <p className="text-muted" style={{ marginBottom: '8px' }}>
              <i className="fas fa-clipboard-list"></i> Aplique una plantilla para iniciar
            </p>
            <div className="mant-plantilla-row">
              <select
                className="form-select"
                value={selectedPlantillaId}
                onChange={(e) => setSelectedPlantillaId(e.target.value)}
              >
                <option value="">Seleccionar plantilla...</option>
                {plantillas.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
              {hasPermission('ordenes_trabajo.create') && (
                <button
                  className="btn btn-primary"
                  onClick={handleCopiarPlantilla}
                  disabled={!selectedPlantillaId || copyingPlantilla}
                >
                  {copyingPlantilla ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-copy"></i>}
                  {' '}Aplicar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Estado vacio con plantilla ya aplicada */}
      {actividades.length === 0 && plantillaId && (
        <div className="actividades-scroll-area">
          <div className="actividades-empty">
            <i className="fas fa-tasks"></i>
            <h4>Sin actividades</h4>
            <p>Las actividades de la plantilla no se encontraron</p>
          </div>
        </div>
      )}

      {/* Vista de paso único */}
      {actividades.length > 0 && act && (
        <>
          <div
            className="actividades-scroll-area"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className={`ot-step-card mant-act-${act.estado}`}>

              {/* Header: número + nombre + badge estado */}
              <div className="ot-step-header">
                <span className={`actividad-orden ot-step-orden ${act.estado === 'completado' ? 'completado' : act.estado === 'omitido' ? 'omitido' : ''}`}>
                  {act.estado === 'completado'
                    ? <i className="fas fa-check"></i>
                    : act.estado === 'omitido'
                      ? <i className="fas fa-forward"></i>
                      : act.orden}
                </span>
                <h3 className="ot-step-nombre">{act.nombre}</h3>
                <span className={`badge ${estadoBadge[act.estado]} ot-step-badge`}>
                  {estadoLabel[act.estado]}
                </span>
              </div>

              {/* Duración */}
              {(act.estado === 'en_progreso' || act.estado === 'completado') && (
                <div className="ot-step-meta">
                  <span className="badge badge-secondary">
                    <i className="fas fa-clock"></i> {getDuracion(act)}
                  </span>
                </div>
              )}

              {/* Descripción */}
              {act.descripcion && (
                <p className="ot-step-descripcion">{act.descripcion}</p>
              )}

              {/* Evidencia — siempre visible */}
              <div className="ot-step-evidencia">
                <EvidenciaGaleria
                  actividadId={act.id}
                  disabled={disabled}
                  onEvidenciaChange={fetchActividades}
                  canEdit={hasPermission('evidencia_actividad.update')}
                  canDelete={hasPermission('evidencia_actividad.delete')}
                />
              </div>

              {/* Observaciones */}
              {act.observaciones && (
                <p className="mant-act-observaciones">
                  <i className="fas fa-comment"></i> {act.observaciones}
                </p>
              )}

              {/* Formulario inline de omisión */}
              {!disabled && omitirActId === act.id && (
                <div className="mant-omitir-form ot-step-omitir-form">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Motivo de omisión..."
                    value={omitirObs}
                    onChange={(e) => setOmitirObs(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && omitirObs.trim()) {
                        handleCambiarEstado(act.id, 'omitido', omitirObs.trim());
                        setOmitirActId(null);
                        setOmitirObs('');
                      }
                    }}
                    autoFocus
                  />
                  <div className="ot-step-omitir-btns">
                    <button
                      className="btn btn-warning ot-step-btn"
                      onClick={() => {
                        if (omitirObs.trim()) {
                          handleCambiarEstado(act.id, 'omitido', omitirObs.trim());
                          setOmitirActId(null);
                          setOmitirObs('');
                        }
                      }}
                      disabled={!omitirObs.trim()}
                    >
                      <i className="fas fa-forward"></i> Confirmar omisión
                    </button>
                    <button
                      className="btn btn-secondary ot-step-btn"
                      onClick={() => { setOmitirActId(null); setOmitirObs(''); }}
                    >
                      <i className="fas fa-times"></i> Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              {!disabled && omitirActId !== act.id && (act.estado === 'pendiente' || act.estado === 'en_progreso') && hasPermission('actividades.execute') && (
                <div className="ot-step-actions">
                  <button
                    className="btn btn-primary ot-step-btn"
                    onClick={() => handleCambiarEstado(act.id, 'completado')}
                  >
                    <i className="fas fa-check"></i> Completar
                  </button>
                  <button
                    className="btn btn-secondary ot-step-btn"
                    onClick={() => setOmitirActId(act.id)}
                  >
                    <i className="fas fa-forward"></i> Omitir
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer de navegación */}
          <div className="ot-step-nav">
            <button
              className="ot-step-nav-btn"
              onClick={handlePrev}
              disabled={safeIndex === 0}
              title="Paso anterior"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            <div className="ot-step-nav-center">
              <div className="ot-step-dots">
                {actividades.map((a, i) => (
                  <button
                    key={a.id}
                    className={`ot-step-dot ot-step-dot--${a.estado}${i === safeIndex ? ' ot-step-dot--active' : ''}`}
                    onClick={() => handleDotClick(i)}
                    title={`Paso ${i + 1}: ${a.nombre}`}
                  />
                ))}
              </div>
              <span className="ot-step-nav-label">Paso {safeIndex + 1} de {total}</span>
            </div>

            <button
              className="ot-step-nav-btn"
              onClick={handleNext}
              disabled={safeIndex === actividades.length - 1}
              title="Paso siguiente"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ActividadesOrdenTrabajoList;
